<template>
  <v-container fluid>
    <!-- Welcome & Search Section (side by side on desktop) -->
    <v-row class="mb-6">
      <!-- Welcome Card (left side) -->
      <v-col cols="12" md="6">
        <v-card class="welcome-card h-100" elevation="4">
          <v-card-text class="text-center py-6">
            <div class="greeting-text">
              <h1 class="text-h3 font-weight-bold mb-2">
                {{ greeting }}, {{ currentUser.username }}!
              </h1>
              <p class="text-h6 mb-4">{{ welcomeMessage }}</p>
              <div class="d-flex justify-center align-center flex-wrap mb-2">
                <v-chip class="ma-1" color="white" label text-color="primary">
                  <v-icon left>mdi-clock-outline</v-icon>
                  {{ currentTime }}
                </v-chip>
                <v-chip class="ma-1" color="white" label text-color="primary">
                  <v-icon left>mdi-calendar</v-icon>
                  {{ currentDate }}
                </v-chip>
                <!-- Move toggle here and set color to 'white' for contrast -->
                <overdue-alert-toggle
                  class="ml-2"
                  :overdue-alert-data="overdueItems.tatAlert"
                  :overdue-max-data="overdueItems.tatMax"
                  :ref-type="departments"
                  :sub-ref-types="subRefTypes"
                  :branch="branches"
                  :stages="stages"
                  :current-user="currentUser"
                  color="white"
                  switch-text-color="primary"
                />
              </div>
            </div>
            <!-- Overdue Statistics -->
            <div class="mt-6 mb-2">
              <div class="d-flex justify-center align-center mt-3">
                <template v-if="overdueLoading">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="32"
                    width="3"
                    class="mr-2"
                  ></v-progress-circular>
                  <span class="font-weight-medium overdue-spinner-text">
                    Checking overdue alerts...
                  </span>
                </template>
                <template v-else>
                  <v-chip
                    v-if="overdueItems.tatAlert.length"
                    color="warning"
                    class="mr-2"
                    label
                    text-color="white"
                    style="font-weight: 500"
                  >
                    <v-icon left small>mdi-alert</v-icon>
                    {{ overdueItems.tatAlert.length }} Alert Level Cases
                  </v-chip>
                  <v-chip
                    v-if="overdueItems.tatMax.length"
                    color="error"
                    class="mr-2"
                    label
                    text-color="white"
                    style="font-weight: 500"
                  >
                    <v-icon left small>mdi-alert-circle</v-icon>
                    {{ overdueItems.tatMax.length }} Max Level Cases
                  </v-chip>
                  <v-chip
                    v-if="
                      !overdueItems.tatAlert.length &&
                      !overdueItems.tatMax.length
                    "
                    color="success"
                    label
                    text-color="white"
                    style="font-weight: 500"
                  >
                    <v-icon left small>mdi-check-circle</v-icon>
                    No Overdue Cases
                  </v-chip>
                </template>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Filter Controls Card (right side) -->
      <v-col cols="12" md="6">
        <v-card
          elevation="2"
          class="h-100 pa-4"
          :class="!filterControlsEnabled ? 'filter-controls-disabled' : ''"
          :style="
            !filterControlsEnabled
              ? 'pointer-events: auto; opacity: 0.5; cursor: not-allowed'
              : ''
          "
          @click="!filterControlsEnabled && showFilterDisabled"
        >
          <template v-if="!filterControlsEnabled">
            <div class="d-flex align-center mb-2">
              <v-icon color="warning" left>mdi-alert</v-icon>
              <span class="font-weight-bold ml-2" style="color: #b26a00">
                Filter Controls are temporarily disabled
              </span>
            </div>
          </template>
          <v-row dense>
            <v-col cols="12" md="4">
              <v-autocomplete
                clearable
                v-model="filters.insurer"
                :items="insurersForFilter"
                item-text="name"
                item-value="id"
                label="Insurer"
                :menu-props="{ maxHeight: '400' }"
                persistent-hint
                @change="
                  filterControlsEnabled
                    ? onInsurerChange()
                    : showFilterDisabled()
                "
                prepend-icon="mdi-domain"
                dense
                :disabled="!filterControlsEnabled"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-autocomplete
                clearable
                v-model="filters.branch"
                :items="branchesForFilter"
                item-text="name"
                item-value="id"
                label="Branch"
                :menu-props="{ maxHeight: '400' }"
                persistent-hint
                @change="
                  filterControlsEnabled
                    ? onBranchChange()
                    : showFilterDisabled()
                "
                prepend-icon="mdi-office-building"
                dense
                :disabled="!filterControlsEnabled"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-autocomplete
                clearable
                v-model="filters.refType"
                :items="departmentsForFilter"
                item-text="name"
                item-value="id"
                label="Department"
                :menu-props="{ maxHeight: '400' }"
                persistent-hint
                @change="
                  filterControlsEnabled ? onDeptChange() : showFilterDisabled()
                "
                prepend-icon="mdi-account-group"
                dense
                :disabled="!filterControlsEnabled"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-autocomplete
                clearable
                v-model="filters.fileStatus"
                :items="stagesForFilter"
                item-text="name"
                item-value="stageCode"
                label="Status"
                :menu-props="{ maxHeight: '400' }"
                persistent-hint
                @change="
                  filterControlsEnabled
                    ? onStatusChange()
                    : showFilterDisabled()
                "
                prepend-icon="mdi-checkbox-marked-circle-outline"
                dense
                :disabled="!filterControlsEnabled"
              />
            </v-col>
            <v-col cols="12" md="8">
              <v-text-field
                clearable
                v-model.lazy="filters.keyword"
                label="Search By Keyword (Vehicle No or AA Ref)"
                @input="
                  filterControlsEnabled
                    ? onKeywordInput($event)
                    : showFilterDisabled()
                "
                prepend-icon="mdi-magnify"
                dense
                :disabled="!filterControlsEnabled"
              />
            </v-col>
          </v-row>
          <v-divider class="my-4"></v-divider>
          <div class="d-flex justify-end">
            <v-btn
              color="primary"
              :disabled="!filterControlsEnabled || !hasAnyFilter"
              large
              elevation="2"
              @click="
                filterControlsEnabled
                  ? goToAdvancedSearch()
                  : showFilterDisabled()
              "
            >
              <v-icon left>mdi-magnify</v-icon>
              Search
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- User Profile and Quick Access Section -->
    <v-row>
      <!-- User Profile Section -->
      <v-col cols="12" md="4">
        <v-card elevation="2" class="h-100">
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-account-circle</v-icon>
            Profile Information
          </v-card-title>
          <v-card-text class="mt-4">
            <v-list dense>
              <v-list-item v-if="currentUser.fullname">
                <v-list-item-icon>
                  <v-icon>mdi-card-account-details</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{
                    currentUser.fullname
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-list-item>
                <v-list-item-icon>
                  <v-icon>mdi-email</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ currentUser.email }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-list-item>
                <v-list-item-icon>
                  <v-icon>mdi-shield-account</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Roles</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip
                      v-for="role in currentUser.roles"
                      :key="role"
                      class="mr-2 mt-2"
                      small
                      color="primary"
                      text-color="white"
                    >
                      {{ role }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>

              <v-list-item
                v-if="currentUser.branches && currentUser.branches.length > 0"
              >
                <v-list-item-icon>
                  <v-icon>mdi-office-building</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Assigned Branches</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip
                      v-for="branch in branchNames"
                      :key="branch.id"
                      class="mr-2 mt-2"
                      small
                      color="secondary"
                      text-color="white"
                    >
                      {{ branch.name }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>

              <v-list-item
                v-if="currentUser.dept && currentUser.dept.length > 0"
              >
                <v-list-item-icon>
                  <v-icon>mdi-domain</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Assigned Departments</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip
                      v-for="dept in deptNames"
                      :key="dept.id"
                      class="mr-2 mt-2"
                      small
                      color="secondary"
                      text-color="white"
                    >
                      {{ dept.name }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Quick Access Section -->
      <v-col cols="12" md="8">
        <v-card elevation="2">
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-lightning-bolt</v-icon>
            Quick Access
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col
                v-for="(item, index) in quickAccessItems"
                :key="index"
                cols="12"
                sm="6"
                md="4"
              >
                <v-card
                  v-if="item.title !== 'Claims'"
                  :to="item.route"
                  hover
                  outlined
                  class="text-center pa-4"
                >
                  <v-icon size="48" color="primary">{{ item.icon }}</v-icon>
                  <div class="title mt-2">{{ item.title }}</div>
                  <div class="caption grey--text">{{ item.description }}</div>
                </v-card>
                <v-card
                  v-else
                  hover
                  outlined
                  class="text-center pa-4 claims-disabled"
                  @click="showComingSoon"
                  style="cursor: not-allowed; opacity: 0.6"
                >
                  <v-icon size="48" color="primary">{{ item.icon }}</v-icon>
                  <div class="title mt-2">{{ item.title }}</div>
                  <div class="caption grey--text">
                    {{ item.description }}{" "}
                    <span class="font-italic">(Coming Soon)</span>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar" :timeout="-1" color="warning" top right>
      <v-icon left color="white" class="ma-2">mdi-alert</v-icon>
      <span class="font-weight-bold">{{ snackbarText }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snackbar = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import BranchDataService from "@/services/BranchDataService";
import DeptDataService from "@/services/DeptDataService";
import StagesDataService from "@/services/StageDataService";
import InsDataService from "@/services/InsDataService";
import OverdueAlertToggle from "@/components/overdue/OverdueAlertToggle.vue";
import TatChartDataService from "@/services/TatChartDataService";
import axios from "axios";
import { apiConfig } from "@/config/api.config";

export default {
  name: "Welcome",
  components: {
    OverdueAlertToggle,
  },
  data() {
    return {
      currentTime: "",
      currentDate: "",
      clockInterval: null,
      branches: [],
      departments: [], // Add this line
      quickAccessItems: [
        {
          title: "Casefiles",
          icon: "mdi-folder-multiple",
          description: "View all casefiles",
          route: "/home",
        },
        {
          title: "Listing",
          icon: "mdi-format-list-bulleted",
          description: "View by Category",
          route: "/listing",
        },
        {
          title: "Profile",
          icon: "mdi-account-circle",
          description: "Your profile",
          route: "/profile",
        },
        // {
        //   title: "Basic Reports",
        //   icon: "mdi-clipboard-file",
        //   description: "View basic reports",
        //   route: "/reports",
        // },
        // {
        //   title: "Detail Reports",
        //   icon: "mdi-clipboard-search",
        //   description: "View detailed reports",
        //   route: "/reportsTwo",
        // },
        {
          title: "Reports Generator",
          icon: "mdi-clipboard-search",
          description: "View detailed reports",
          route: "/reportsV1",
        },
        {
          title: "Claims",
          icon: "mdi-cash-multiple",
          description: "Manage claims",
          route: "/claims",
          // No need to add a flag, handled in template
        },
      ],
      // Add advanced search properties
      searchLoading: false,
      advancedSearch: {
        keyword: "",
        dept: null,
        branch: null,
        status: null,
        insurer: null, // Add this line
      },
      stages: [], // For filtering by status
      insurers: [], // Add this line
      tatCharts: [], // Add this for TAT chart data
      snackbar: false,
      snackbarText: "",
      filters: {
        insurer: "",
        branch: "",
        refType: "",
        fileStatus: "",
        keyword: "",
      },
      subRefTypes: [],
      members: [],
      handlers: [],
      filterControlsEnabled: false, // Set to true to enable the module
      overdueItems: {
        tatAlert: [],
        tatMax: [],
      },
      overdueLoading: false, // <-- Add this line
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    branchNames() {
      return this.currentUser.branches
        .map((branchId) => this.branches.find((b) => b.id === branchId))
        .filter((branch) => branch); // Filter out any undefined values
    },
    deptNames() {
      return this.currentUser.dept
        .map((deptId) => this.departments.find((d) => d.id === deptId))
        .filter((dept) => dept);
    },
    greeting() {
      const hour = new Date().getHours();
      if (hour < 12) return "Good Morning";
      if (hour < 17) return "Good Afternoon";
      return "Good Evening";
    },
    welcomeMessage() {
      const messages = [
        "Welcome to your Document Management Workspace",
        "Here's your personalized workspace",
        "Your document control center awaits",
        "Managing documents made simple",
        "Access all your documents in one place",
        "Streamline your document workflow today",
        "Your digital document hub is ready",
        "Efficiently manage your documents here",
        "Welcome to your secure document portal",
      ];
      return messages[Math.floor(Math.random() * messages.length)];
    },
    // Add computed properties for the advanced search
    departmentsForFilter() {
      if (!this.departments || this.departments.length === 0) return [];

      const user = this.$store.state.auth.user;
      if (!user) return [];

      // If user has admin, branch manager, or clerk role, show all departments
      if (
        user.roles.some((role) => ["ADMIN", "MANAGER", "CLERK"].includes(role))
      ) {
        const sortedDepts = [...this.departments].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        return [{ id: "all", name: "All Departments" }, ...sortedDepts];
      }

      // Otherwise, filter departments based on user's department access
      const userDepts = Array.isArray(user.dept) ? user.dept : [user.dept];

      const filteredDepts = this.departments
        .filter((dept) => userDepts.some((userDept) => userDept === dept.id))
        .sort((a, b) => a.name.localeCompare(b.name));

      return [{ id: "all", name: "All Departments" }, ...filteredDepts];
    },

    branchesForFilter() {
      if (!this.branches || this.branches.length === 0) return [];

      const user = this.$store.state.auth.user;
      if (!user) return [];

      // If user has admin, branch manager, manager, or clerk role, show all branches
      if (
        user.roles.some((role) => ["ADMIN", "MANAGER", "CLERK"].includes(role))
      ) {
        const sortedBranches = [...this.branches].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        return [{ id: "all", name: "All Branches" }, ...sortedBranches];
      }

      // Otherwise, filter branches based on user's branch access
      const userBranches = Array.isArray(user.branches)
        ? user.branches
        : [user.branches];

      const filteredBranches = this.branches
        .filter((branch) =>
          userBranches.some((userBranch) => userBranch === branch.id)
        )
        .sort((a, b) => a.name.localeCompare(b.name));

      return [{ id: "all", name: "All Branches" }, ...filteredBranches];
    },

    stagesForFilter() {
      if (!this.stages || this.stages.length === 0) return [];
      const sortedStages = [...this.stages]
        .filter((stage) => stage.stageCode && stage.name)
        .sort((a, b) => a.id - b.id);
      return [{ stageCode: "all", name: "All Statuses" }, ...sortedStages];
    },

    insurersForFilter() {
      if (!this.insurers || this.insurers.length === 0) return [];
      return [...this.insurers].sort((a, b) => a.name.localeCompare(b.name));
    },

    isAdvancedSearchValid() {
      return Boolean(
        this.advancedSearch.keyword ||
          this.advancedSearch.dept ||
          this.advancedSearch.branch ||
          this.advancedSearch.status ||
          this.advancedSearch.insurer
      );
    },
    hasAnyFilter() {
      // Enable the button only if at least one filter is set
      return Object.values(this.filters).some(
        (v) => v !== null && v !== "" && v !== undefined
      );
    },
  },
  methods: {
    retrieveBranches() {
      BranchDataService.getAll()
        .then((response) => {
          this.branches = response.data;
        })
        .catch((e) => {
          console.log(e);
        });
    },
    retrieveDepartments() {
      DeptDataService.getAll()
        .then((response) => {
          this.departments = response.data;
        })
        .catch((e) => {
          console.log(e);
        });
    },
    retrieveStages() {
      StagesDataService.getAll()
        .then((response) => {
          this.stages = response.data;
        })
        .catch((e) => {
          console.log(e);
        });
    },
    retrieveInsurers() {
      InsDataService.getAll()
        .then((response) => {
          this.insurers = response.data.sort((a, b) =>
            a.name.localeCompare(b.name)
          );
        })
        .catch((e) => {
          console.log(e);
        });
    },
    updateDateTime() {
      const now = new Date();
      this.currentTime = new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).format(now);

      this.currentDate = new Intl.DateTimeFormat("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }).format(now);
    },
    async fetchTatCharts() {
      try {
        const response = await TatChartDataService.getAll();
        this.tatCharts = response.data;
      } catch (e) {
        this.tatCharts = [];
      }
    },
    getTatValues(insurerId, subRefTypeId) {
      if (!insurerId || !subRefTypeId) return null;
      const filteredTat = this.tatCharts.find(
        (tat) => tat.insId === insurerId && tat.subDeptId === subRefTypeId
      );
      return filteredTat
        ? {
            tatAlert: parseInt(filteredTat.tatAlert),
            tatMax: parseInt(filteredTat.tatMax),
          }
        : null;
    },
    async fetchOverdueItems() {
      this.overdueLoading = true; // <-- Set loading true before fetch
      try {
        const user = this.currentUser;
        const params = {
          userId: user.id,
          roles: JSON.stringify(user.roles),
          dept: JSON.stringify(user.dept),
          branches: JSON.stringify(user.branches),
        };
        const res = await axios.get(`${apiConfig.baseURL}/casefiles/overdue`, {
          params,
        });
        this.overdueItems = res.data;
      } catch (e) {
        this.overdueItems = { tatAlert: [], tatMax: [] };
      } finally {
        this.overdueLoading = false; // <-- Set loading false after fetch
      }
    },
    async performAdvancedSearch() {
      if (!this.isAdvancedSearchValid) return;

      this.searchLoading = true;
      try {
        const searchParams = {};

        // Only store essential search parameters
        if (this.advancedSearch.keyword) {
          searchParams.keyword = this.advancedSearch.keyword;
        }

        if (this.advancedSearch.dept && this.advancedSearch.dept !== "all") {
          searchParams.dept = this.advancedSearch.dept;
        }

        if (
          this.advancedSearch.branch &&
          this.advancedSearch.branch !== "all"
        ) {
          searchParams.branch = this.advancedSearch.branch;
        }

        if (
          this.advancedSearch.status &&
          this.advancedSearch.status !== "all"
        ) {
          searchParams.status = this.advancedSearch.status;
        }

        if (
          this.advancedSearch.insurer &&
          this.advancedSearch.insurer !== "all"
        ) {
          searchParams.insurer = this.advancedSearch.insurer;
        }

        // Store minimal search criteria
        try {
          const storageStr = JSON.stringify(searchParams);
          if (storageStr.length <= 4096) {
            // Check if within reasonable size
            localStorage.setItem("lastSearchCriteria", storageStr);
          }
        } catch (storageError) {
          console.warn("Unable to save search criteria:", storageError);
        }

        await this.$router.push({
          name: "advancedSearch",
          query: searchParams,
        });
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        this.searchLoading = false;
      }
    },

    resetAdvancedSearch() {
      this.advancedSearch = {
        keyword: "",
        dept: null,
        branch: null,
        status: null,
        insurer: null, // Add this line
      };
      // Clear saved search criteria
      localStorage.removeItem("lastSearchCriteria");
    },

    // Add this method to load saved search criteria
    loadSavedSearchCriteria() {
      try {
        const savedCriteria = localStorage.getItem("lastSearchCriteria");
        if (savedCriteria) {
          const criteria = JSON.parse(savedCriteria);
          this.advancedSearch = {
            keyword: criteria.keyword || "",
            dept: criteria.dept || null,
            branch: criteria.branch || null,
            status: criteria.status || null,
            insurer: criteria.insurer || null, // Add this line
          };
        }
      } catch (e) {
        console.error("Error loading saved search criteria:", e);
      }
    },
    updateAdvancedSearch(val) {
      this.advancedSearch = { ...val };
    },
    showComingSoon() {
      this.snackbarText = "Claims feature is coming soon!";
      this.snackbar = true;
    },
    showFilterDisabled() {
      this.snackbarText =
        "Filter Controls are temporarily disabled. This module will be activated soon.";
      this.snackbar = true;
    },
    onBranchChange() {
      // No adjuster field to clear anymore
      this.onFilterChange();
    },
    onDeptChange() {
      // No subRefType field to clear anymore
      this.onFilterChange();
    },
    onInsurerChange() {
      // Ensure reactivity for hasAnyFilter
      this.filters = { ...this.filters };
      this.onFilterChange();
    },
    onStatusChange() {
      // If "All Statuses" selected, clear fileStatus filter for search
      if (this.filters.fileStatus === "all") {
        this.filters.fileStatus = "";
      }
      // Ensure reactivity for hasAnyFilter
      this.filters = { ...this.filters };
      this.onFilterChange();
    },
    onKeywordInput(val) {
      // Ensure the filter is updated as user types, not just on blur
      this.filters.keyword = val;
      // Do not call onFilterChange here to avoid unnecessary filter triggers
    },
    async onFilterChange(newFilters) {
      // Accept both direct and event-based calls
      if (newFilters) {
        this.filters = { ...newFilters };
      }
      // No server fetch here, just update filters
    },
    goToAdvancedSearch() {
      // Build query from filters, omitting empty/null values and mapping keys for backend compatibility
      const query = {};
      // Always send keyword, even if empty string, to match Home.vue logic
      if (typeof this.filters.keyword !== "undefined")
        query.keyword = this.filters.keyword ? this.filters.keyword.trim() : "";
      if (this.filters.refType && this.filters.refType !== "all")
        query.dept = this.filters.refType;
      if (this.filters.branch && this.filters.branch !== "all")
        query.branch = this.filters.branch;
      if (this.filters.fileStatus && this.filters.fileStatus !== "all")
        query.status = this.filters.fileStatus;
      if (this.filters.insurer && this.filters.insurer !== "all")
        query.insurer = this.filters.insurer;

      // If only keyword is present and empty, treat as no filters
      const hasRealFilter = Object.keys(query).some(
        (k) => k !== "keyword" && query[k]
      );
      if (!hasRealFilter && (!query.keyword || query.keyword === "")) {
        // Prevent navigation if no filters at all
        this.snackbarText = "Please enter at least one filter.";
        this.snackbar = true;
        return;
      }

      this.$router.push({
        name: "advancedSearch",
        query,
      });
    },
  },
  mounted() {
    this.retrieveBranches();
    this.retrieveDepartments();
    this.retrieveStages();
    this.retrieveInsurers();
    this.updateDateTime();
    this.clockInterval = setInterval(this.updateDateTime, 60000);
    this.loadSavedSearchCriteria();
    // Fetch and compute overdue items for the current user
    this.fetchOverdueItems();
  },
  beforeDestroy() {
    if (this.clockInterval) clearInterval(this.clockInterval);
  },
};
</script>

<style scoped>
.welcome-card {
  background: linear-gradient(135deg, #1976d2 0%, #64b5f6 100%);
  position: relative;
  overflow: hidden;
  height: 100%; /* Ensure full height */
  display: flex;
  flex-direction: column;
  justify-content: center; /* Center content vertically */
}

.welcome-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0) 100%
  );
  pointer-events: none;
}

.greeting-text {
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.greeting-text h1 {
  color: white;
  letter-spacing: 0.5px;
}

.greeting-text p {
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
}

.h-100 {
  height: 100%;
}

/* Add these new styles for the advanced search section */
.welcome-card + .v-row .v-card {
  transition: all 0.3s ease;
}

/* Ensure inputs in advanced search look consistent */
.v-input__slot {
  min-height: 40px !important;
}

.v-chip {
  margin: 4px !important;
}

.v-chip--close .v-icon {
  font-size: 16px;
  margin-right: -6px;
}

/* Responsive adjustments for mobile */
@media (max-width: 960px) {
  .welcome-card {
    margin-bottom: 16px;
  }

  .greeting-text h1 {
    font-size: 1.75rem !important;
  }
}

.claims-disabled {
  pointer-events: auto !important;
}

.filter-controls-disabled {
  /* Optional: further visual cue for disabled card */
  filter: grayscale(0.3) brightness(0.95);
  user-select: none;
}

.overdue-spinner-text {
  color: #222 !important; /* High contrast for visibility */
  font-size: 1.1rem;
  letter-spacing: 0.5px;
}
</style>
